import type { BuiltInTool } from './types'

/**
 * Enterprise context: a JSON value one run stores and a later run reads. The schedule input is fixed, and
 * execution metadata stays inside one pipeline, so this is the store a task uses to keep its place.
 *
 * A value is bookkeeping. It is not a memory note, and it is not a place for a secret. Last write wins.
 * `os.set_context` and `os.delete_context` are idempotent on the tool call, the way `os.record_kpi_reading` is.
 */
const KEY = {
    type: 'string',
    description: 'A short key, such as sitemap:https://example.com/pricing. No spaces. Not a secret.',
} as const

const KEY_ERRORS = [
    { code: 'key_required', when: '`key` is missing.', handling: 'tool_error' },
    {
        code: 'key_invalid',
        when: '`key` is empty, longer than 512 characters, or contains whitespace or a control character.',
        handling: 'tool_error',
    },
] as const

export const OS_CONTEXT_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.set_context',
        family: 'os.context',
        summary: 'Store a JSON value for the enterprise under a key, so a later run can read it.',
        description:
            'Store value at key for this enterprise. A later call with the same key replaces the value. Any later run of any task in the enterprise can read it, and a task version that declares a prefix of the key receives it in input.context when a run starts. value is JSON and at most 16KB. This is bookkeeping between runs, not memory, and not a place for a secret. A repeat of the same tool call returns the value that call stored and does not write again.',
        inputSchema: {
            type: 'object',
            properties: {
                key: KEY,
                value: {
                    description: 'Any JSON value, at most 16KB. Not a secret.',
                },
            },
            required: ['key', 'value'],
            additionalProperties: false,
        },
        returns: '`{ key, value }`: the key and the JSON value now stored for it.',
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        retryNote:
            'Keyed on the tool call, like every other os.* write tool: a repeat of the same call stores once and returns the value that call stored.',
        errors: [
            ...KEY_ERRORS,
            { code: 'value_required', when: '`value` is missing.', handling: 'tool_error' },
            { code: 'value_not_json', when: '`value` is not JSON.', handling: 'tool_error' },
            { code: 'value_too_large', when: '`value` is over 16KB.', handling: 'tool_error' },
        ],
        examples: [{ input: { key: 'sitemap:https://example.com/pricing', value: { status: 'absorbed' } } }],
        public: true,
    },
    {
        name: 'os.get_context',
        family: 'os.context',
        summary: 'Read one enterprise context value, or null when the key has never been set.',
        description:
            'Read the JSON value stored at key for this enterprise. Returns value null when the key has never been set. A run whose task declares a prefix of the key already has it in input.context; this reads one key that was not loaded, or a key another run has changed since this run started.',
        inputSchema: {
            type: 'object',
            properties: { key: KEY },
            required: ['key'],
            additionalProperties: false,
        },
        returns: '`{ key, value }`. `value` is null when the key has never been set.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [...KEY_ERRORS],
        examples: [{ input: { key: 'sitemap:https://example.com/pricing' } }],
        public: true,
    },
    {
        name: 'os.delete_context',
        family: 'os.context',
        summary: 'Remove one enterprise context key. A missing key is a success.',
        description:
            'Remove key from this enterprise. A key that is not there is a success. Used when the thing the key tracked has gone, such as a URL that left the sitemap. A repeat of the same tool call returns the result that call stored and does not delete a key a later call has set.',
        inputSchema: {
            type: 'object',
            properties: { key: KEY },
            required: ['key'],
            additionalProperties: false,
        },
        returns: '`{ key, deleted }`. `deleted` is false when the key was not there.',
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        retryNote:
            'Keyed on the tool call, like every other os.* write tool: a repeat of the same call removes the key once and returns that call’s result.',
        errors: [...KEY_ERRORS],
        examples: [{ input: { key: 'sitemap:https://example.com/pricing' } }],
        public: true,
    },
]
